import type { FC, ReactElement, ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { Button, Dropdown, Select, Spin, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import {
    EllipsisOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    LoadingOutlined,
    PlusCircleOutlined,
    RightOutlined,
} from '@ant-design/icons';
import SearchBox from 'components/molecules/SearchBox';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import {
    TPO_CALENDAR_ADD_EVENTS_BTN,
    TPO_CALENDAR_ADD_EVENTS_FROM_PLAN_ITEM,
    TPO_CALENDAR_ADD_NEW_EVENT_ITEM,
    TPO_CALENDAR_COLUMNS_SELECT,
    TPO_CALENDAR_FILTER,
    TPO_CALENDAR_FILTER_RIBBON,
    TPO_CALENDAR_GROUP_BY_SELECT,
    TPO_CALENDAR_INFEASIBLE_FACTORS_BTN,
    TPO_CALENDAR_OPTIMIZE_PLAN_BTN,
    TPO_CALENDAR_PLAN_ACTIONS_BTN,
    TPO_CALENDAR_SEARCH_INPUT,
    TPO_CALENDAR_TOOLBAR,
    tpoOptionTestId,
} from '../../config/testIds';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { GlobalFilterState } from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { InfeasibilityStatus } from '../../types/infeasiblePlan';
import { useEditSession } from '../EditSession/EditSessionContext';
import { applyEditLockToMenu, EDIT_PLAN_MENU_KEY } from '../EditSession/utils/applyEditLockToMenu';
import EditPlanMenuLabel from '../EditSession/components/EditPlanMenuLabel';
import { buildPlanActionMenu } from '../utils/planActionMenu';
import { useResetRuleSession } from '../../rules/store/useResetRuleSession';
import { sendNotification } from 'utils/helperMethods';
import { resolveFieldLabel } from '../../utils/resolveFieldLabel';
import { TPOGEN_EVENT_ADD_CLICKED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';

const COLUMN_SELECT_MAX_VISIBLE_TAGS = 1;

const SCENARIO_COMPARISON_NEW_PATH = '/tpo-gen/scenario-comparison/new';

const renderColumnSelectOverflow = (omittedValues: readonly unknown[]) => `+${omittedValues.length} selected`;

const renderColumnSelectTag = (props: { label: ReactNode }) => <Tag>{props.label}</Tag>;

const renderColumnSelectDropdown = (menu: ReactElement) => <div className="flex flex-col gap-y-2">{menu}</div>;

const INFEASIBLE_SPINNER = <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />;
const INFEASIBLE_CHEVRON_STYLE = { fontSize: 12 };
const TOOLTIP_INNER_STYLE = { color: '#333' };

export interface CalendarToolbarProps {
    planId: string;
    selectedColumnKeys: string[];
    setSelectedColumnKeys: (keys: string[]) => void;
    searchKeyword: string;
    setSearchKeyword: (kw: string) => void;
    groupBy: string;
    setGroupBy: (val: string) => void;
    filterState: GlobalFilterState;
    showChanges: boolean;
    onToggleShowChanges: () => void;
    onShowDeletePlan: () => void;
    onShowDeleteAllPromotions: () => void;
    onShowImportModal: () => void;
    onShowOptimizePlan: () => void;
    isOptimizationInProgress?: boolean;
    optimizationCompleted?: boolean;
    onShowOptimisationResults: () => void;
    onShowSaveAs: () => void;
    onShowSaveAsOriginal: () => void;
    onShowExportModal: () => void;
    /** True while a calendar export for this plan is still running. */
    isExportPending?: boolean;
    isDataLoading?: boolean;
    /** The last run found no feasible solution, so its factors can be inspected. */
    isInfeasible?: boolean;
    infeasibilityStatus?: InfeasibilityStatus;
    onShowInfeasibleDrawer?: () => void;
}

const CalendarToolbar: FC<CalendarToolbarProps> = ({
    planId,
    selectedColumnKeys,
    setSelectedColumnKeys,
    setSearchKeyword,
    groupBy,
    setGroupBy,
    filterState,
    showChanges,
    onToggleShowChanges,
    onShowDeletePlan,
    onShowDeleteAllPromotions,
    onShowImportModal,
    onShowOptimizePlan,
    isOptimizationInProgress = false,
    optimizationCompleted = false,
    onShowOptimisationResults,
    onShowSaveAs,
    onShowSaveAsOriginal,
    onShowExportModal,
    isExportPending = false,
    isDataLoading = false,
    isInfeasible = false,
    infeasibilityStatus = InfeasibilityStatus.IDLE,
    onShowInfeasibleDrawer,
}) => {
    const navigate = useNavigate();
    const tpoGenConfig = useTpoGenConfig();
    const {
        allDefaultColumns,
        planActionMenuItems,
        planActionMenuViews,
        calendarFilterKeys,
        fieldValueLabelMaps,
        infeasiblePlanConfig,
    } = tpoGenConfig;
    const infeasibleLabels = infeasiblePlanConfig.labels;
    const isDiagnosingInfeasibility = infeasibilityStatus === InfeasibilityStatus.POLLING;
    const [search, setSearch] = useState('');
    const [addEventsOpen, setAddEventsOpen] = useState(false);
    const { setActiveView, setEditEventId, optimizedPlanId, showOptimizedView, currentPlanDetails } = usePlanDetailsContext();
    const { canEdit, isLockEnabled, isHeldByOther, holder, openEditPrompt } = useEditSession();
    const isOperationalPlan = Boolean(currentPlanDetails?.is_operational_plan);
    const activePlanId = showOptimizedView && optimizedPlanId ? optimizedPlanId : planId;
    const isOptimizedViewActive = showOptimizedView && !!optimizedPlanId;
    // The optimized calendar offers a different action set from the original one
    // (see `planActionMenuViews` in the customer config).
    const planActionSurface = isOptimizedViewActive ? 'calendar_optimized' : 'calendar_original';
    const { resetEventSession } = useResetRuleSession();
    const nonRemovableColumnKeys = useMemo(
        () => new Set(allDefaultColumns.filter((col) => col.required).map((col) => col.key)),
        [allDefaultColumns],
    );
    const columnLabelByKey = useMemo(
        () =>
            allDefaultColumns.reduce<Record<string, string>>((acc, col) => {
                acc[col.key] = col.label;
                return acc;
            }, {}),
        [allDefaultColumns],
    );
    const filterKeyMap = useMemo(() => new Map(calendarFilterKeys.map((fk) => [fk.value, fk])), [calendarFilterKeys]);
    const filterDataParser = useCallback(
        (rawItems: string[], resolveLabelFrom?: string) =>
            rawItems.map((name) => ({
                label: resolveFieldLabel(name, fieldValueLabelMaps, resolveLabelFrom),
                value: name,
            })),
        [fieldValueLabelMaps],
    );
    const renderFilterAttribute = useCallback(
        (key: string, attr: string | number) => (
            <span className="py-1.5">
                {resolveFieldLabel(String(attr), fieldValueLabelMaps, filterKeyMap.get(key)?.resolveLabelFrom)}
            </span>
        ),
        [fieldValueLabelMaps, filterKeyMap],
    );

    const handleColumnSelectionChange = useCallback(
        (value: string[]) => {
            const removedRequiredColumnKey = selectedColumnKeys.find(
                (key) => nonRemovableColumnKeys.has(key) && !value.includes(key),
            );

            if (removedRequiredColumnKey) {
                sendNotification('error', `${columnLabelByKey[removedRequiredColumnKey]} column cannot be deselected.`);
                setSelectedColumnKeys([...selectedColumnKeys]);
                return;
            }

            setSearch('');
            setSearchKeyword('');
            setSelectedColumnKeys(value);
        },
        [columnLabelByKey, nonRemovableColumnKeys, selectedColumnKeys, setSearchKeyword, setSelectedColumnKeys],
    );

    const resolvedMenuItems = useMemo(
        () =>
            planActionMenuItems.map((section) =>
                section.map((item) => {
                    if (!item || !('key' in item)) return item;

                    const withTestId = {
                        ...item,
                        'data-testid': tpoOptionTestId(TPO_CALENDAR_PLAN_ACTIONS_BTN, String(item.key)),
                    };

                    if (withTestId.key === 'show_changes') {
                        return {
                            ...withTestId,
                            label: showChanges ? 'Hide Changes' : 'Show Changes',
                            icon: showChanges ? <EyeInvisibleOutlined /> : <EyeOutlined />,
                        };
                    }
                    if (withTestId.key === 'optimisation_results') {
                        return {
                            ...withTestId,
                            disabled: !optimizationCompleted || !isOptimizedViewActive,
                        };
                    }
                    // Save as Original promotes the optimized plan, so it is only
                    // available while the optimized calendar is on screen. A config-level
                    // `disabled: true` always wins over that runtime check.
                    if (withTestId.key === 'save_as_original') {
                        const isConfigDisabled = 'disabled' in withTestId && !!withTestId.disabled;
                        return {
                            ...withTestId,
                            disabled: isConfigDisabled || !isOptimizedViewActive,
                        };
                    }
                    // Client-side duplicate guard; a second tab is not covered.
                    if (withTestId.key === 'export') {
                        const isConfigDisabled = 'disabled' in withTestId && !!withTestId.disabled;
                        return { ...withTestId, disabled: isConfigDisabled || isExportPending };
                    }
                    return withTestId;
                }),
            ),
        [planActionMenuItems, showChanges, optimizationCompleted, isOptimizedViewActive, isExportPending],
    );

    // Edit-lock gating runs after the runtime checks so it can only ever remove
    // availability from what they already decided.
    const editSessionLabels = tpoGenConfig.labels.editSession;
    const lockedMenuItems = useMemo(
        () =>
            applyEditLockToMenu(resolvedMenuItems, {
                canEdit,
                isLockEnabled,
                isHeldByOther,
                isOperationalPlan,
                editLabel: (
                    <EditPlanMenuLabel
                        label={editSessionLabels.editPlan}
                        holder={isHeldByOther ? holder : null}
                        heldByOtherPrefix={editSessionLabels.heldByOtherPrefix}
                        disabledTooltip={
                            isOperationalPlan ? tpoGenConfig.labels.planDetail.operationalPlanTooltip : null
                        }
                    />
                ),
                baseTestId: TPO_CALENDAR_PLAN_ACTIONS_BTN,
            }),
        [
            resolvedMenuItems,
            canEdit,
            isLockEnabled,
            isHeldByOther,
            isOperationalPlan,
            holder,
            editSessionLabels,
            tpoGenConfig.labels.planDetail.operationalPlanTooltip,
        ],
    );

    // Visibility runs last: only here does the injected `edit_plan` row exist to
    // be filtered out. This is also what flattens the sections for AntD.
    const visibleMenuItems = useMemo(
        () => buildPlanActionMenu(lockedMenuItems, planActionSurface, planActionMenuViews),
        [lockedMenuItems, planActionSurface, planActionMenuViews],
    );

    const groupByOptions = useMemo(
        () =>
            tpoGenConfig.calendarGroupByOptions.map((opt) => ({
                ...opt,
                'data-testid': tpoOptionTestId(TPO_CALENDAR_GROUP_BY_SELECT, String(opt.value)),
            })),
        [tpoGenConfig.calendarGroupByOptions],
    );

    const handlePlanActionClick: MenuProps['onClick'] = useCallback(
        ({ key }: { key: string }) => {
            if (key === EDIT_PLAN_MENU_KEY) {
                openEditPrompt();
                return;
            }
            if (key === 'show_changes') {
                onToggleShowChanges();
                return;
            }
            if (key === 'delete_plan') {
                onShowDeletePlan();
                return;
            }
            if (key === 'delete_all_promotions') {
                onShowDeleteAllPromotions();
                return;
            }
            if (key === 'compare_scenarios') {
                // `/new` is the only unsaved-report path registered as a route; the bare
                // `/tpo-gen/scenario-comparison` path falls through to the catch-all redirect.
                // The dim keys must match `ScenarioComparison`'s LocationState: they seed the
                // scope the base/comparison plan pickers query, so a mismatch leaves both empty.
                navigate(SCENARIO_COMPARISON_NEW_PATH, {
                    state: {
                        createNew: true,
                        // `activePlanId`, not `planId`: the Optimized switch decides which of the
                        // two plans is on screen, and that is the one being compared.
                        baseScenario: {
                            planId: activePlanId,
                            planName: currentPlanDetails?.plan_name ?? activePlanId,
                        },
                        dim1_labels: currentPlanDetails?.dim1_attrs ?? [],
                        dim2_labels: currentPlanDetails?.dim2_attrs ?? [],
                    },
                });
                return;
            }
            if (key === 'optimisation_results') {
                onShowOptimisationResults();
                return;
            }
            if (key === 'save_as') {
                onShowSaveAs();
                return;
            }
            if (key === 'save_as_original') {
                onShowSaveAsOriginal();
                return;
            }
            if (key === 'export') {
                onShowExportModal();
                return;
            }
        },
        [
            openEditPrompt,
            onToggleShowChanges,
            onShowDeletePlan,
            onShowDeleteAllPromotions,
            onShowOptimisationResults,
            onShowSaveAs,
            onShowSaveAsOriginal,
            onShowExportModal,
            navigate,
            activePlanId,
            currentPlanDetails,
        ],
    );

    return (
        <>
            <div
                className="flex flex-row justify-between items-center gap-x-4 flex-wrap my-4"
                data-testid={TPO_CALENDAR_TOOLBAR}
            >
                <div>
                    <Select
                        mode="tags"
                        data-testid={TPO_CALENDAR_COLUMNS_SELECT}
                        style={{ width: '15rem' }}
                        value={selectedColumnKeys}
                        onChange={handleColumnSelectionChange}
                        disabled={isDataLoading}
                        options={allDefaultColumns.map((col) => ({
                            label: col.label,
                            value: col.key,
                            'data-testid': tpoOptionTestId(TPO_CALENDAR_COLUMNS_SELECT, col.key),
                        }))}
                        maxTagCount={COLUMN_SELECT_MAX_VISIBLE_TAGS}
                        maxTagPlaceholder={renderColumnSelectOverflow}
                        tagRender={renderColumnSelectTag}
                        dropdownRender={renderColumnSelectDropdown}
                    />
                </div>
                <div className="flex flex-row justify-start items-center gap-x-4">
                    <SearchBox
                        search={search}
                        setSearch={setSearch}
                        onSearch={() => setSearchKeyword(search)}
                        disabled={isDataLoading}
                        data-testid={TPO_CALENDAR_SEARCH_INPUT}
                    />
                    <div className="flex flex-row justify-start items-center gap-x-2">
                        <p className="text-[#52525C] text-xs">{'Group by:'}</p>
                        <Select
                            data-testid={TPO_CALENDAR_GROUP_BY_SELECT}
                            style={{ width: '10rem' }}
                            value={groupBy}
                            options={groupByOptions}
                            onChange={(value) => setGroupBy(value)}
                            disabled={isDataLoading}
                        />
                    </div>
                    <Dropdown
                        trigger={['hover']}
                        open={addEventsOpen}
                        onOpenChange={setAddEventsOpen}
                        menu={{
                            items: [
                                {
                                    label: tpoGenConfig.labels.events.addNewEvent,
                                    key: 'add_new_event',
                                    'data-testid': TPO_CALENDAR_ADD_NEW_EVENT_ITEM,
                                    onClick: () => {
                                        trackTpoGenEvent(
                                            TPOGEN_EVENT_ADD_CLICKED,
                                            TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR,
                                            {
                                                plan_id: planId,
                                            },
                                        );
                                        // Clear any stale cross-section working set from a prior
                                        // event/promo session so a brand-new event starts clean
                                        // (the rule store outlives individual event forms).
                                        resetEventSession();
                                        setActiveView('event');
                                        setEditEventId(null);
                                    },
                                } as NonNullable<MenuProps['items']>[number],
                                {
                                    label: tpoGenConfig.labels.events.addEventsFromPlan,
                                    key: 'add_events_from_plan',
                                    'data-testid': TPO_CALENDAR_ADD_EVENTS_FROM_PLAN_ITEM,
                                    onClick: () => onShowImportModal(),
                                } as NonNullable<MenuProps['items']>[number],
                            ],
                        }}
                        disabled={isDataLoading || !canEdit}
                    >
                        <Button
                            data-testid={TPO_CALENDAR_ADD_EVENTS_BTN}
                            disabled={isDataLoading || !canEdit}
                            onClick={() => setAddEventsOpen(true)}
                        >
                            <div className="flex flex-row justify-start items-center gap-x-2 font-semibold text-sm">
                                <PlusCircleOutlined />
                                <p>{tpoGenConfig.labels.events.addEvents}</p>
                            </div>
                        </Button>
                    </Dropdown>
                    <NewGlobalFilter
                        availableFilters={filterState.availableFilters}
                        allCheckedAttribute={filterState.allCheckedAttribute}
                        allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                        applyFilterObject={filterState.applyFilterObject}
                        resetFilterObject={filterState.resetFilterObject}
                        onSubmitHandler={filterState.onFilterSaveClickHandler}
                        url={tpoGenUrls.getCalendarDataFilterAttributes(activePlanId)}
                        selectedFilter={filterState.selectedFilter}
                        setSelectedFilter={filterState.setSelectedFilter}
                        tempApplyFilterObject={filterState.tempApplyFilterObject}
                        setTempApplyFilterObject={filterState.setTempApplyFilterObject}
                        apiVariables={filterState.filterApiVariables}
                        filterDimensionInfo={tpoGenConfig.calendarFilterDimensionInfo}
                        showAttributePanelToLeft
                        filterIcon={<FilterIcon />}
                        disabled={isDataLoading}
                        dataParser={filterDataParser}
                        data-testid={TPO_CALENDAR_FILTER}
                    />
                    <Button
                        type="primary"
                        data-testid={TPO_CALENDAR_OPTIMIZE_PLAN_BTN}
                        onClick={onShowOptimizePlan}
                        disabled={!canEdit || isDataLoading || isOptimizationInProgress || isOptimizedViewActive}
                        loading={isOptimizationInProgress}
                    >
                        {tpoGenConfig.labels.optimizePlan.toolbarButton}
                    </Button>
                    {isInfeasible && isOptimizedViewActive ? (
                        <Tooltip
                            title={isDiagnosingInfeasibility ? infeasibleLabels.buttonTooltipWhilePolling : ''}
                            placement="bottom"
                            color="white"
                            overlayInnerStyle={TOOLTIP_INNER_STYLE}
                        >
                            {/* A disabled button emits no mouse events, so the tooltip
                                explaining why it is disabled needs its own hover target. */}
                            <span className="inline-flex">
                                <Button
                                    data-testid={TPO_CALENDAR_INFEASIBLE_FACTORS_BTN}
                                    data-state-diagnosing={isDiagnosingInfeasibility ? 'true' : 'false'}
                                    disabled={isDiagnosingInfeasibility}
                                    onClick={onShowInfeasibleDrawer}
                                >
                                    <div className="flex flex-row justify-start items-center gap-x-2">
                                        {isDiagnosingInfeasibility ? INFEASIBLE_SPINNER : null}
                                        <p>{infeasibleLabels.buttonText}</p>
                                        <RightOutlined style={INFEASIBLE_CHEVRON_STYLE} />
                                    </div>
                                </Button>
                            </span>
                        </Tooltip>
                    ) : null}
                    <Dropdown
                        menu={{
                            items: visibleMenuItems,
                            onClick: handlePlanActionClick,
                        }}
                        trigger={['click']}
                        overlayStyle={{ width: '12rem', borderRadius: '0.5rem' }}
                        disabled={isDataLoading}
                    >
                        <Button
                            className="!px-2 !py-1"
                            data-testid={TPO_CALENDAR_PLAN_ACTIONS_BTN}
                            disabled={isDataLoading}
                        >
                            <div className="flex flex-row justify-start items-center">
                                <EllipsisOutlined />
                            </div>
                        </Button>
                    </Dropdown>
                </div>
            </div>
            <div className="mb-3 mt-2">
                <FilterRibbon
                    allCheckedAttribute={filterState.allCheckedAttribute}
                    allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                    applyFilterObject={filterState.applyFilterObject}
                    resetFilterObject={filterState.resetFilterObject}
                    onSubmitHandler={filterState.onFilterSaveClickHandler}
                    filterKeys={calendarFilterKeys}
                    renderAttribute={renderFilterAttribute}
                    data-testid={TPO_CALENDAR_FILTER_RIBBON}
                />
            </div>
        </>
    );
};

CalendarToolbar.displayName = 'CalendarToolbar';
export default CalendarToolbar;
